import React, { useEffect, useState } from "react";
import {
    Button,
    Col,
    Icon,
    Input,
    message,
    Modal,
    Radio,
    Row,
    Select,
    Spin,
    Tooltip
} from "antd";
import style from "./index.module.scss";
import {
    addOrUpdateVoteTactics,
    publishVotePaper,
    votePaperInfo,
    VoteState,
    VoteType,
    VoteTypeMap
} from "service/vote";
import { useAsyncFn } from "react-use";
import _ from "underscore";
import { errorMessage, toNumber } from "service/service";
import useRequestFn from "utils/useRequestFn";

interface SettingModalProps {
    weekTimeId: number;
    id?: number;
    visible: boolean;
    onOk: () => void;
    onCancel: () => void;
    onShowVote: (id: number) => void;
}

export default function SettingModal({
    id,
    weekTimeId,
    visible,
    onCancel,
    onOk,
    onShowVote
}: SettingModalProps) {
    const [{ loading }, doSubmit] = useAsyncFn(addOrUpdateVoteTactics);

    const [title, setTitle] = useState("");
    const [type, setType] = useState<VoteType>(VoteType.Single);
    const [answersNumber, setAnswersNumber] = useState(4);
    const [validityTime, setValidityTime] = useState(0);

    const [getData, { loading: loadingData, data }] =
        useRequestFn(votePaperInfo);

    // 编辑时回显数据
    useEffect(() => {
        if (!visible || !id) return;

        getData(id).then(data => {
            setTitle(data.title);
            setType(data.type);
            setAnswersNumber(data.answersNumber);
            setValidityTime(data.validityTime || 0);
        });
    }, [visible]);

    async function saveData() {
        const ret = await doSubmit({
            id,
            weekTimeId,
            title: title.trim(),
            type,
            answersNumber,
            validityTime
        });

        if (ret.status !== 200) {
            message.error(errorMessage(ret));
        } else {
            onOk();
        }
        return ret;
    }

    async function saveAndStart() {
        try {
            const ret = await saveData();
            if (ret.status !== 200) throw ret;
            await publishVotePaper(toNumber(ret.data.message));
            onShowVote(toNumber(ret.data.message));
        } catch (e) {
            message.error(errorMessage(e));
        }
    }

    function reset() {
        setTitle("");
        setType(VoteType.Single);
        setAnswersNumber(4);
        setValidityTime(0);
    }

    function renderFooter() {
        if (!id || data?.status === VoteState.Idle) {
            return (
                <div>
                    <Button onClick={saveData} loading={loading}>
                        保存
                    </Button>
                    <Button
                        type="primary"
                        onClick={saveAndStart}
                        loading={loading}>
                        立即开始
                    </Button>
                </div>
            );
        }

        return (
            <div>
                <Button onClick={onCancel}>取消</Button>
                <Button type="primary" onClick={saveData} loading={loading}>
                    确定
                </Button>
            </div>
        );
    }

    function getContainer(): any {
        return document.querySelector(".PopupContainer");
    }

    return (
        <Modal
            visible={visible}
            title={id ? "投票设置" : "新建投票"}
            className={style.root}
            width={600}
            onCancel={onCancel}
            onOk={onOk}
            afterClose={reset}
            destroyOnClose
            getContainer={getContainer}
            footer={renderFooter()}>
            <Spin spinning={loadingData}>
                <Row>
                    <Col span={8} className={style.label}>
                        名称：
                    </Col>
                    <Col span={16} className={style.element}>
                        <Input.TextArea
                            rows={1}
                            className={style.input}
                            placeholder="请输入活动名称，最多可输入 500 个汉字"
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            maxLength={500}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col span={8} className={style.label}>
                        类型：
                    </Col>
                    <Col span={16} className={style.element}>
                        <Radio.Group
                            value={type}
                            onChange={e => setType(e.target.value)}
                            disabled={data?.status === VoteState.InProgress}>
                            {_.map(VoteTypeMap, (item, key) => (
                                <Radio key={key} value={toNumber(key)}>
                                    {item}投票
                                </Radio>
                            ))}
                        </Radio.Group>
                    </Col>
                </Row>

                <Row
                    style={{
                        display: type !== VoteType.Judge ? "block" : "none"
                    }}>
                    <Col span={8} className={style.label}>
                        选项数量：
                    </Col>
                    <Col span={16} className={style.element}>
                        <Select
                            className={style.select}
                            dropdownClassName={style.dropdown}
                            value={answersNumber}
                            onChange={setAnswersNumber}
                            disabled={data?.status === VoteState.InProgress}
                            getPopupContainer={getContainer}>
                            <Select.Option className={style.option} value={2}>
                                2
                            </Select.Option>
                            <Select.Option className={style.option} value={3}>
                                3
                            </Select.Option>
                            <Select.Option className={style.option} value={4}>
                                4
                            </Select.Option>
                        </Select>{" "}
                        &nbsp;项
                    </Col>
                </Row>

                <Row>
                    <Col span={8} className={style.label}>
                        活动时长：
                    </Col>
                    <Col span={16} className={style.element}>
                        <Select
                            className={style.select}
                            dropdownClassName={style.dropdown}
                            value={validityTime}
                            onChange={setValidityTime}
                            getPopupContainer={getContainer}>
                            <Select.Option className={style.option} value={0}>
                                不限
                            </Select.Option>
                            <Select.Option className={style.option} value={3}>
                                3
                            </Select.Option>
                            <Select.Option className={style.option} value={5}>
                                5
                            </Select.Option>
                            <Select.Option className={style.option} value={10}>
                                10
                            </Select.Option>
                            <Select.Option className={style.option} value={15}>
                                15
                            </Select.Option>
                        </Select>{" "}
                        &nbsp;分钟&nbsp;&nbsp;
                        <Tooltip
                            title="系统根据设置的活动时长自动结束投票，活动时长为“不限”时，表示需要教师手动结束投票"
                            getPopupContainer={getContainer}>
                            <Icon
                                type="exclamation-circle"
                                className={style.notice}
                            />
                        </Tooltip>
                    </Col>
                </Row>
            </Spin>
        </Modal>
    );
}
